import type { ReactNode } from "react";
import MarkdownRenderer from "./MarkdownRenderer";
import { parseHintBlocks } from "../utils/hintBlocks";

export type HintTone = "section" | "question" | "tip" | "warning" | "important";

const TONE_CARD: Record<HintTone, string> = {
  section:
    "bg-[#F8F6F3] border-[#E8E0D5] border-l-[#1B365D]",
  question:
    "bg-[#EFF6FF] border-[#D6E4F5] border-l-[#1B365D]",
  tip: "bg-[#ECFDF7] border-[#B7EDE0] border-l-[#2A9D8F]",
  warning: "bg-[#FEF2F2] border-[#FECACA] border-l-[#DC2626]",
  important: "bg-[#FFF6ED] border-[#FAD7B3] border-l-[#F4A261]",
};

const MICRO_LABEL =
  "text-[11px] font-bold uppercase tracking-[0.06em] text-[#8A6A45]";

/**
 * Card chrome shared by section / question / typed hint panels.
 * Visual language matches GuidanceSections + eligibility context cards:
 * navy left accent, warm or cool fill, bronze micro-labels.
 */
export const HintCard = ({
  eyebrow,
  title,
  tone = "section",
  maxHeightClass,
  children,
  testId,
}: {
  eyebrow?: string;
  title?: string;
  tone?: HintTone;
  maxHeightClass?: string;
  children?: ReactNode;
  testId?: string;
}) => (
  <section
    data-testid={testId}
    className={`rounded-lg border border-l-4 p-3.5 ${TONE_CARD[tone]} ${
      maxHeightClass ? `${maxHeightClass} overflow-y-auto scrollbar-thin scrollbar-thumb-slate-400` : ""
    }`}
  >
    {eyebrow ? <div className={`${MICRO_LABEL} mb-1`}>{eyebrow}</div> : null}
    {title ? (
      <div className="text-sm font-semibold text-[#1B365D] mb-2 [&_p]:mb-0">
        <MarkdownRenderer content={title} />
      </div>
    ) : null}
    {children}
  </section>
);

/**
 * Lays out hint/instruction copy as discrete labelled blocks. Short
 * unlabelled notes render as a single body, matching GuidanceSections.
 */
const HintBlocks = ({
  text,
  className = "",
}: {
  text?: string | null;
  className?: string;
}) => {
  const blocks = parseHintBlocks(text);
  if (blocks.length === 0) return null;

  return (
    <div className={`flex flex-col gap-3 text-left ${className}`}>
      {blocks.map((block, index) => {
        const useMicroLabel = Boolean(
          block.label && block.label.length <= 42
        );
        return (
          <div key={`${block.label ?? "text"}-${index}`}>
            {useMicroLabel ? (
              <div className={`${MICRO_LABEL} mb-1.5`}>{block.label}</div>
            ) : block.label ? (
              <div className="text-sm font-semibold text-[#1B365D] mb-1.5">
                {block.label}
              </div>
            ) : null}
            {block.body ? (
              <div className="text-[13px] leading-[20px] text-[#1B365D] [&_p]:mb-1.5 [&_p:last-child]:mb-0">
                <MarkdownRenderer content={block.body} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default HintBlocks;
